import React from 'react'

import {
  Badge,
  Box,
  Button,
  Flex,
  Grid,
  GridItem,
  HStack,
  Link,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
} from '@chakra-ui/react'
import dayjs from 'dayjs'
import { useBreadcrumb } from 'src/hooks/useBreadcrumb'

import { IcfDocumentRevisionStatusBadge } from '../../icf-document/components/icf-document-status-badge'
import { RequiredPatientSignBadge } from '../../icf-document/components/required-patient-sign-badge'
import { icfDocumentTypeText } from '../../icf-document/entity'
import { IcfDocumentSet } from '../entity'

type Props = {
  icfDocumentSet: IcfDocumentSet | undefined
  listPath: string
  canEdit: boolean
  canEnable: boolean
  navigateToEdit: () => void
  getFilePreviewUrl: (revisionUid: string) => string
  onEnable: () => Promise<void>
  shouldShowHospitalName: boolean
  hospitalName: string
}

export const IcfDocumentSetDetailRenewal: React.FC<Props> = ({
  icfDocumentSet,
  listPath,
  canEdit,
  canEnable,
  navigateToEdit,
  getFilePreviewUrl,
  onEnable,
  shouldShowHospitalName,
  hospitalName,
}) => {
  const { renderBreadcrumb } = useBreadcrumb()

  if (!icfDocumentSet) {
    return null
  }

  return (
    <Flex direction="column" h="full" py="6">
      <Flex justify="space-between" wrap="wrap" rowGap={2} px="6">
        {renderBreadcrumb([
          { text: '文書セット一覧', link: listPath },
          { text: '文書セット詳細', isCurrentPage: true },
        ])}
        {canEdit && <Button onClick={navigateToEdit}>設定編集</Button>}
        {canEnable && <Button onClick={onEnable}>文書セットを有効化</Button>}
      </Flex>

      <Box flex="1" overflow="auto" px="6">
        <Grid mt="8" templateColumns="max-content 1fr" columnGap={6} rowGap={4}>
          {shouldShowHospitalName && (
            <>
              <GridItem>
                <Text fontSize="sm" fontWeight="medium" color="gray.600">
                  医療機関
                </Text>
              </GridItem>
              <GridItem>
                <Text>{hospitalName}</Text>
              </GridItem>
            </>
          )}
          <GridItem>
            <Text fontSize="sm" fontWeight="medium" color="gray.600">
              文書セットID
            </Text>
          </GridItem>
          <GridItem>
            <HStack spacing="4" align="center">
              <Text color={icfDocumentSet.enabled ? undefined : 'gray.400'}>
                {icfDocumentSet.numberingId}
              </Text>
              {!icfDocumentSet.enabled && (
                <Badge
                  w="64px"
                  h="25px"
                  display="flex"
                  justifyContent="center"
                  alignItems="center"
                  color="gray.400"
                  bg="gray.100"
                  fontWeight="bold"
                  fontSize="10px"
                >
                  無効
                </Badge>
              )}
            </HStack>
          </GridItem>
          <GridItem>
            <Text fontSize="sm" fontWeight="medium" color="gray.600">
              文書セット名
            </Text>
          </GridItem>
          <GridItem>
            <Text color={icfDocumentSet.enabled ? undefined : 'gray.400'}>
              {icfDocumentSet.name}
            </Text>
          </GridItem>
        </Grid>

        <Box mt="8" w="full">
          <Text fontWeight="bold">設定文書</Text>
          <Box w="full" overflow="auto">
            <Table mt="2">
              <Thead>
                <Tr>
                  <Th>ステータス</Th>
                  <Th>文書ID</Th>
                  <Th>文書名</Th>
                  <Th>版数</Th>
                  <Th>文書の種類</Th>
                  <Th>公開日時</Th>
                </Tr>
              </Thead>
              <Tbody>
                {icfDocumentSet.icfDocuments
                  .sort((a, b) => a.index - b.index)
                  .map(document => (
                    <Tr key={document.uid}>
                      <Td w="120px" minW="120px">
                        <IcfDocumentRevisionStatusBadge
                          status={document.status}
                        />
                      </Td>
                      <Td
                        w="100px"
                        minW="100px"
                        color={
                          document.status === 'Disabled'
                            ? 'gray.400'
                            : undefined
                        }
                      >
                        {document.numberingId}
                      </Td>
                      <Td minW="120px">
                        <Link
                          href={getFilePreviewUrl(
                            document.latestPublishedRevisionUid,
                          )}
                          target="_blank"
                          rel="noopener noreferrer"
                          color="blue.500"
                        >
                          {document.latestPublishedName}
                        </Link>
                      </Td>
                      <Td
                        w="100px"
                        minW="100px"
                        color={
                          document.status === 'Disabled'
                            ? 'gray.400'
                            : undefined
                        }
                      >
                        {document.latestPublishedVersion}
                      </Td>
                      <Td
                        minW="160px"
                        color={
                          document.status === 'Disabled'
                            ? 'gray.400'
                            : undefined
                        }
                      >
                        <HStack spacing="8px">
                          <span>{icfDocumentTypeText[document.type]}</span>{' '}
                          {document.latestIsRequiredPatientSign && (
                            <RequiredPatientSignBadge />
                          )}
                        </HStack>
                      </Td>
                      <Td
                        minW="120px"
                        color={
                          document.status === 'Disabled'
                            ? 'gray.400'
                            : undefined
                        }
                      >
                        {dayjs(document.latestPublishedAt).format(
                          'YYYY/MM/DD HH:mm:ss',
                        )}
                      </Td>
                    </Tr>
                  ))}
              </Tbody>
            </Table>
          </Box>
        </Box>
      </Box>
    </Flex>
  )
}
